import { Button, Col, Row, Table, Tooltip } from "antd";
import _findIndex from "lodash/findIndex";
import _forEach from "lodash/forEach";
import _isBoolean from "lodash/isBoolean";
import _isEmpty from "lodash/isEmpty";
import _isNumber from "lodash/isNumber";
import _map from "lodash/map";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { replaceIfNull } from "../../utils/utils";
import CustomIcon from "../CustomIcon/CustomIcon";
import "./TransferTable.scss";

const syncEmptyStrings = ["", "-", "n/a", "na", "nan", "null", "undefined"];

const syncHasValue = value =>
  value !== undefined && value !== null && !syncEmptyStrings.includes(`${value}`.trim().toLowerCase());

const renderSyncDisplayValue = (value, fallback) => (value === undefined || value === null ? fallback : `${value}`);

const syncNormalizeValue = value => {
  if (Array.isArray(value)) return value.map(syncNormalizeValue).join("|");
  if (value && typeof value === "object") {
    if (Object.prototype.hasOwnProperty.call(value, "value")) return syncNormalizeValue(value.value);
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") return value ? "1" : "0";
  if (!syncHasValue(value)) return "";

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return `${Number(numericValue.toFixed(6))}`;
  const stringValue = `${value}`.trim().toLowerCase();
  if (["yes", "true", "y"].includes(stringValue)) return "1";
  if (["no", "false", "n"].includes(stringValue)) return "0";
  return stringValue;
};

const hasUpdatedChange = record =>
  record.canApply !== false &&
  !record.isEqual &&
  record.updatedValue !== undefined &&
  syncNormalizeValue(record.updatedValue) !== syncNormalizeValue(record.dpmValue);

const TransferTable = ({
  scroll,
  size,
  source,
  current,
  previewRows,
  onPreview,
  onApplyRows,
  onSuccess,
  onCancel,
  loading: externalLoading,
}) => {
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const applyingRef = useRef(false);
  const isBudSyncMode = Array.isArray(previewRows) || !!onPreview || !!onApplyRows;
  const controlsDisabled = externalLoading || loading || applying;
  const getBudUpdatedValue = record => {
    if (!isBudSyncMode) return record.snowflakeValue;
    if (Object.prototype.hasOwnProperty.call(record, "applyValue")) return record.applyValue;
    return record.budValue === undefined ? null : record.budValue;
  };
  const canApplyRow = record => !isBudSyncMode || record.canApply !== false;

  useEffect(() => {
    const loadRows = async () => {
      if (Array.isArray(previewRows)) {
        setDataSource(previewRows);
        setLoading(false);
        return;
      }
      if (onPreview) {
        setLoading(true);
        const rows = await onPreview();
        setDataSource(rows || []);
        setLoading(false);
      }
    };
    if (isBudSyncMode) {
      loadRows();
      return;
    }

    const _dataSource = [];
    Object.entries(source).forEach(([key, value]) => {
      const obj = {};
      obj.id = key;
      obj.fieldName = value.label;
      obj.snowflakeValue = value.value;
      obj.currentValue = current[key];
      _dataSource.push(obj);
    });
    setDataSource(_dataSource);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _selectAllValues = () => {
    if (controlsDisabled) return;
    const _dataSource = _map(dataSource, d =>
      d.isEqual || !canApplyRow(d)
        ? d
        : {
            ...d,
            updatedValue: getBudUpdatedValue(d),
            updatedDisplayValue: d.budDisplayValue ?? d.snowflakeValue,
          },
    );

    setDataSource([..._dataSource]);
  };

  const _resetValues = () => {
    if (controlsDisabled) return;
    const _dataSource = _map(dataSource, d => ({
      ...d,
      updatedValue: undefined,
      updatedDisplayValue: undefined,
    }));

    setDataSource([..._dataSource]);
  };

  const _updateSelectedValues = () => {
    if (onApplyRows) {
      if (controlsDisabled || applyingRef.current) return;
      applyingRef.current = true;
      setApplying(true);
      const finishApply = () => {
        applyingRef.current = false;
        setApplying(false);
      };
      try {
        const result = onApplyRows(dataSource.filter(hasUpdatedChange));
        if (result && typeof result.then === "function") result.then(finishApply, finishApply);
        else finishApply();
      } catch (err) {
        finishApply();
        throw err;
      }
      return;
    }

    const _selectedValues = {};
    _forEach(dataSource, d => {
      if (d.updatedValue !== undefined && d.updatedValue !== null) {
        _selectedValues[d.id] = d.updatedValue;
      }
    });

    if (onSuccess) onSuccess(_selectedValues);
  };

  const _selectValue = record => {
    if (controlsDisabled) return;
    const _dataSource = dataSource;
    const _index = _findIndex(_dataSource, { id: record.id });
    if (!canApplyRow(_dataSource[_index])) return;
    _dataSource[_index] = {
      ..._dataSource[_index],
      updatedValue: getBudUpdatedValue(_dataSource[_index]),
      updatedDisplayValue: _dataSource[_index].budDisplayValue ?? _dataSource[_index].snowflakeValue,
    };

    setDataSource([..._dataSource]);
  };

  const _deselectValue = record => {
    if (controlsDisabled) return;
    const _dataSource = dataSource;
    const _index = _findIndex(_dataSource, { id: record.id });
    _dataSource[_index] = {
      ..._dataSource[_index],
      updatedValue: isBudSyncMode ? _dataSource[_index].dpmValue : undefined,
      updatedDisplayValue: isBudSyncMode ? _dataSource[_index].dpmDisplayValue : undefined,
    };

    setDataSource([..._dataSource]);
  };

  const hasChanges = dataSource.some(hasUpdatedChange);

  const columns = [
    {
      title: t("TransferTable.FieldName"),
      dataIndex: "fieldName",
      key: "fieldName",
      fixed: "left",
      width: 200,
      render: text => replaceIfNull(text, "N/A"),
    },
    {
      title: t("TransferTable.BudValue"),
      dataIndex: isBudSyncMode ? "budDisplayValue" : "snowflakeValue",
      key: "snowflakeValue",
      width: 150,
      render: text => (isBudSyncMode ? renderSyncDisplayValue(text, "N/A") : replaceIfNull(text, "N/A")),
    },
    {
      title: t("TransferTable.DpmValue"),
      dataIndex: isBudSyncMode ? "dpmDisplayValue" : "currentValue",
      key: "currentValue",
      width: 150,
      render: text => (isBudSyncMode ? renderSyncDisplayValue(text, "N/A") : replaceIfNull(text, "N/A")),
    },
    {
      title: t("TransferTable.Action"),
      align: "center",
      width: 100,
      render: (text, record) => {
        const _disabled =
          !_isNumber(record.updatedValue) && !_isBoolean(record.updatedValue) && _isEmpty(record.updatedValue);
        const _emptySnowflakeValue =
          !_isNumber(record.snowflakeValue) && !_isBoolean(record.snowflakeValue) && _isEmpty(record.snowflakeValue);
        const keepLocalDisabled =
          controlsDisabled || (isBudSyncMode ? record.isEqual || !hasUpdatedChange(record) : _disabled);
        const useBudDisabled = isBudSyncMode
          ? controlsDisabled || record.isEqual || hasUpdatedChange(record) || !canApplyRow(record)
          : controlsDisabled || _emptySnowflakeValue || !_disabled;
        if (!isBudSyncMode) {
          return (
            <>
              <Button
                icon={<CustomIcon name="LeftOutlined" />}
                disabled={_disabled}
                onClick={() => _deselectValue(record)}
              />
              <Button
                icon={<CustomIcon name="RightOutlined" />}
                disabled={useBudDisabled}
                onClick={() => _selectValue(record)}
              />
            </>
          );
        }
        return (
          <>
            <Tooltip title={t("TransferTable.KeepLocalValue")}>
              <Button
                className="sync-action-button"
                icon={<CustomIcon name="LeftOutlined" />}
                disabled={keepLocalDisabled}
                onClick={() => _deselectValue(record)}
              />
            </Tooltip>
            <Tooltip title={t("TransferTable.UseBudValue")}>
              <Button
                className="sync-action-button"
                icon={<CustomIcon name="RightOutlined" />}
                disabled={useBudDisabled}
                onClick={() => _selectValue(record)}
              />
            </Tooltip>
          </>
        );
      },
    },
    {
      title: t("TransferTable.UpdatedValue"),
      dataIndex: "updatedValue",
      key: "updatedValue",
      fixed: "right",
      width: 150,
      render: (text, record) => (isBudSyncMode ? renderSyncDisplayValue(record.updatedDisplayValue, "") : text || ""),
    },
  ];

  return (
    <div className="transfer-table">
      <Row justify="end" gutter={[8, 16]} className="row-vertical-padding-8">
        {isBudSyncMode && (
          <Col xs={24} sm={24} md={6} lg={5} xl={5}>
            <Button block disabled={controlsDisabled} onClick={_resetValues}>
              {t("TransferTable.Reset")}
            </Button>
          </Col>
        )}
        <Col xs={24} sm={24} md={6} lg={5} xl={5}>
          <Button block type="primary" disabled={controlsDisabled} onClick={_selectAllValues}>
            {t("TransferTable.PullAll")}
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={dataSource}
        columns={columns}
        size={size}
        loading={externalLoading || loading}
        rowKey="id"
        scroll={scroll}
        pagination={false}
        rowClassName={record => (record.isEqual ? "sync-row-muted" : "")}
      />
      <Row justify="space-around" className="mg-top-20">
        {onCancel && (
          <Col xs={24} sm={24} md={6} lg={5} xl={5}>
            <Button block disabled={controlsDisabled} onClick={onCancel}>
              {t("Buttons.Cancel")}
            </Button>
          </Col>
        )}
        <Col xs={24} sm={24} md={6} lg={5} xl={5}>
          <Button
            block
            type="primary"
            disabled={controlsDisabled || (isBudSyncMode && !hasChanges)}
            onClick={_updateSelectedValues}
          >
            {t("Buttons.Update")}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

TransferTable.defaultProps = {
  rowSelection: {},
  size: "middle",
  scroll: { x: 750, y: 400 },
};

TransferTable.propTypes = {
  rowSelection: PropTypes.shape({
    onSelect: PropTypes.func,
    onSelectAll: PropTypes.func,
  }),
  size: PropTypes.string,
  scroll: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  source: PropTypes.object,
  current: PropTypes.object,
  previewRows: PropTypes.array,
  onPreview: PropTypes.func,
  onApplyRows: PropTypes.func,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
};

export default TransferTable;
